/**
 * audience-tool-content.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Copy éditorial sectoriel pour les pages /[audience]/[outil].
 *
 * Chaque entrée est identifiée par la clé `[audienceId]-[toolId]`.
 */

import type { AudienceId } from '@shared/constants/audiences';
import type { ToolId } from '@shared/constants/tools-catalog';

export interface AudienceToolContent {
  key: string;
  pageTitle: string;
  metaDescription: string;
  eyebrow: string;
  headline: string;
  headlineAccent: string;
  intro: string;
  benefits: [string, string, string];
  cta: string;
  trustNote?: string;
}

const CONTENT: AudienceToolContent[] = [
  /* ─────────────────────────── PARTICULIERS ──────────────────────────── */
  {
    key: 'particuliers-calculer-mes-aides',
    pageTitle: 'Calculer mes aides rénovation — Particuliers | Solution Éco France',
    metaDescription:
      "Simulez en 2 minutes vos aides MaPrimeRénov', CEE, éco-PTZ et TVA 5,5 %. Résultat personnalisé selon votre logement et vos revenus. Sans inscription.",
    eyebrow: 'Aides rénovation, Particuliers',
    headline: 'Vos aides rénovation chiffrées',
    headlineAccent: 'en 2 minutes chrono.',
    intro:
      "Aides nationales, prime énergie, prêt à taux zéro, TVA réduite… mises bout à bout, ces aides couvrent jusqu'à 90 % du coût des travaux pour les ménages modestes. On calcule votre montant selon votre logement, vos revenus et vos travaux. Sans inscription, sans démarchage.",
    benefits: [
      'Aides nationales + prime énergie + prêt à taux zéro cumulés',
      'Reste à charge affiché en temps réel',
      'Professionnel certifié inclus dans le devis',
    ],
    cta: 'Calculer mes aides gratuitement',
    trustNote: 'Sans inscription, sans engagement, résultat immédiat',
  },

  /* ─────────────────────────── PROFESSIONNELS ─────────────────────────── */
  {
    key: 'professionnels-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Professionnel — Estimez vos primes | Solution Éco France',
    metaDescription:
      'Votre SIRET suffit. En 60 secondes, estimez vos primes CEE applicables à votre activité professionnelle. Résultat chiffré, sans inscription.',
    eyebrow: 'Diagnostic, Primes, Professionnels',
    headline: 'Vos primes en 60 secondes,',
    headlineAccent: 'juste votre SIRET.',
    intro:
      "TPE, PME, indépendant : vos locaux et équipements peuvent vous rapporter des primes. Avec votre SIRET, on identifie ce qui s'applique à votre activité et on vous donne une estimation chiffrée.",
    benefits: [
      'Résultat chiffré en 60 secondes',
      'Primes adaptées à votre code NAF',
      'Aucune inscription requise',
    ],
    cta: 'Lancer mon diagnostic',
    trustNote: 'Gratuit, confidentiel, sans engagement',
  },
  {
    key: 'professionnels-comparateur-energie',
    pageTitle: "Comparateur d'énergie Professionnel — Courtier B2B | Solution Éco France",
    metaDescription:
      "Comparez toutes les offres gaz et électricité pour votre entreprise. Courtier indépendant des fournisseurs d'énergie, audit gratuit de vos factures. Économisez en moyenne 18 %.",
    eyebrow: 'Courtage énergie, Professionnel',
    headline: 'Réduisez vos factures énergie',
    headlineAccent: 'sans changer de fournisseur par obligation.',
    intro:
      "Aucun lien avec un fournisseur, aucune offre exclusive. On audite vos factures, on compare toutes les offres du marché, on vous présente celles qui collent à votre profil. Vous tranchez. Rien ne change tant que vous n'avez pas signé.",
    benefits: [
      'Audit factures gratuit (puissance, options)',
      'Toutes les offres du marché comparées',
      'Aucune bascule automatique',
    ],
    cta: 'Comparer mes offres',
    trustNote: "Courtier indépendant des fournisseurs d'énergie, modèle économique transparent",
  },
  {
    key: 'professionnels-calculer-mes-aides',
    pageTitle: 'Calculer les aides rénovation pour vos locaux pro | Solution Éco France',
    metaDescription:
      "Vous êtes propriétaire d'un local professionnel (cabinet, commerce, agence) ? Identifiez les aides disponibles pour vos travaux.",
    eyebrow: 'Aides rénovation, Locaux professionnels',
    headline: 'Vos aides locaux pro chiffrées',
    headlineAccent: 'à partir de votre SIRET.',
    intro:
      "Commerce, cabinet, agence, atelier : vos locaux ouvrent droit à des primes (isolation, chauffage, éclairage, pilotage), à des avantages fiscaux et à des prêts dédiés. Avec votre SIRET, on liste ce qui s'applique à votre activité.",
    benefits: [
      'Primes cumulables + bonus en vigueur',
      'Avantage fiscal pour équipements chaleur',
      'Prêts dédiés sans garantie',
    ],
    cta: 'Identifier les aides pour mes locaux',
    trustNote: 'Gratuit, sans inscription, diagnostic 60 secondes',
  },

  /* ─────────────────────────── ENTREPRISES ────────────────────────────── */
  {
    key: 'entreprises-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Entreprise — Estimez vos primes | Solution Éco France',
    metaDescription:
      'Estimez les primes CEE applicables à votre entreprise (bâtiments, équipements, process industriels). Résultat chiffré en 60 secondes avec votre SIRET.',
    eyebrow: 'Diagnostic, Primes, Entreprises',
    headline: 'Vos primes industrielles',
    headlineAccent: 'chiffrées en 60 secondes.',
    intro:
      'ETI, grand compte : vos sites de production, entrepôts et bâtiments tertiaires peuvent ouvrir droit à des primes. À partir de votre SIRET, on dresse la liste des opérations applicables et on chiffre une fourchette utilisable pour votre business case.',
    benefits: [
      'Primes adaptées à votre activité',
      'Fourchette chiffrée pour votre business case',
      'Équipe dédiée grands comptes',
    ],
    cta: 'Lancer le diagnostic',
    trustNote: 'Confidentiel, Accord de confidentialité disponible',
  },
  {
    key: 'entreprises-comparateur-energie',
    pageTitle: 'Courtage énergie HTA Entreprise — Comparateur | Solution Éco France',
    metaDescription:
      "Optimisez vos achats d'énergie de vos grands sites. Courtier indépendant des fournisseurs d'énergie, audit des tarifs, comparaison de toutes les offres. Référent dédié grands comptes.",
    eyebrow: 'Courtage énergie, Entreprises',
    headline: 'Optimisez vos achats énergie,',
    headlineAccent: "avec un courtier indépendant des fournisseurs d'énergie.",
    intro:
      "Vos contrats d'énergie pèsent lourd. On analyse ce que vous payez aujourd'hui, on compare avec les offres du marché, on vous dit ce qui est le plus intéressant. Aucun changement sans votre accord.",
    benefits: [
      'Audit complet de votre profil de consommation',
      'Offres indexées, fixes et sur-mesure',
      'Référent dédié, Accord de confidentialité disponible',
    ],
    cta: 'Demander un audit',
    trustNote: 'Premiers éléments chiffrés sous 15 jours ouvrés',
  },
  {
    key: 'entreprises-decret-tertiaire',
    pageTitle: 'Décret Tertiaire Entreprise — Trajectoire −40 % | Solution Éco France',
    metaDescription:
      "Calculez votre trajectoire obligatoire (−40 % 2030, −50 % 2040, −60 % 2050) et obtenez un plan d'actions pour vos bâtiments tertiaires ≥ 1 000 m².",
    eyebrow: 'Obligation réglementaire, Conformité, Entreprises',
    headline: 'Votre trajectoire de réduction',
    headlineAccent: 'calculée en 3 minutes.',
    intro:
      'À partir de 1 000 m² de bureaux ou commerces, vous êtes concerné. En 3 minutes, on calcule vos objectifs de réduction pour 2030, 2040 et 2050, et on vous dit par où commencer.',
    benefits: [
      'Trajectoire chiffrée −40/−50/−60 %',
      "Plan d'actions prioritaires généré automatiquement",
      'Export prêt pour la direction RSE',
    ],
    cta: 'Calculer ma trajectoire',
    trustNote: 'Gratuit, Données confidentielles',
  },

  /* ─────────────────────────── TERTIAIRE ──────────────────────────────── */
  {
    key: 'tertiaire-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Tertiaire — Primes BAT-* | Solution Éco France',
    metaDescription:
      'Estimez les primes applicables à vos immeubles tertiaires. Résultat chiffré avec votre SIRET en 60 secondes.',
    eyebrow: 'Diagnostic, Primes, Tertiaire',
    headline: 'Vos primes tertiaires',
    headlineAccent: 'chiffrées par bâtiment.',
    intro:
      "Bureaux, commerces, hôtels : vos bâtiments ouvrent droit à des primes — pompe à chaleur, pilotage intelligent, isolation, éclairage LED. Avec votre SIRET, on liste les opérations qui s'appliquent et on chiffre une fourchette.",
    benefits: [
      'Primes tertiaires ciblées',
      'Bonus en vigueur intégrés',
      'Résultat exploitable pour votre comité énergie',
    ],
    cta: 'Lancer le diagnostic',
    trustNote: 'Gratuit, Sans inscription, Confidentiel',
  },
  {
    key: 'tertiaire-decret-tertiaire',
    pageTitle: 'Décret Tertiaire — Trajectoire immeubles bureaux | Solution Éco France',
    metaDescription:
      "Conformité pour vos immeubles tertiaires ≥ 1 000 m² : trajectoire -40/-50/-60 %, plan d'actions financé par les primes.",
    eyebrow: 'Obligation réglementaire, Immeubles bureaux',
    headline: 'Votre trajectoire,',
    headlineAccent: 'sécurisée et financée.',
    intro:
      'À partir de 1 000 m² de tertiaire, vous devez déclarer vos consommations chaque année avec un objectif de −40 % en 2030. On calcule votre trajectoire selon les règles officielles, on classe vos leviers par efficacité et on chiffre les primes qui financent chaque opération.',
    benefits: [
      'Trajectoire -40 / -50 / -60 % chiffrée',
      "Plan d'actions financé par les primes",
      'Défense en cas de contrôle',
    ],
    cta: 'Calculer ma trajectoire',
    trustNote: 'Gratuit, Confidentiel, Dépôt sécurisé',
  },
  {
    key: 'tertiaire-comparateur-energie',
    pageTitle: 'Comparateur énergie Tertiaire — Multi-sites | Solution Éco France',
    metaDescription:
      "Comparez les offres élec/gaz pour vos immeubles tertiaires. Audit multi-sites, optimisation profil de puissance, courtier indépendant des fournisseurs d'énergie.",
    eyebrow: 'Courtage énergie, Multi-sites, Tertiaire',
    headline: 'Vos contrats énergie tertiaires,',
    headlineAccent: 'optimisés et consolidés.',
    intro:
      "Pour vos immeubles de bureaux multi-sites, on audite vos contrats, on repère les sur-puissances et on remet l'ensemble en concurrence. Courtier indépendant des fournisseurs d'énergie. Bascule uniquement si vous validez.",
    benefits: [
      'Audit multi-sites complet',
      'Mise en concurrence consolidée',
      'Bascule par lots, planifiée',
    ],
    cta: 'Lancer le diagnostic factures',
    trustNote: "Courtier indépendant des fournisseurs d'énergie · Référent dédié · Sans engagement",
  },

  /* ─────────────────────────── INDUSTRIE ──────────────────────────────── */
  {
    key: 'industrie-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Industriel — Primes IND | Solution Éco France',
    metaDescription:
      'Estimez les primes applicables à votre site industriel (utilités, process, bâti). Diagnostic 60 secondes avec votre SIRET.',
    eyebrow: 'Diagnostic, Primes, Industrie',
    headline: 'Vos primes industrielles',
    headlineAccent: 'chiffrées par poste.',
    intro:
      "Air comprimé, vapeur, froid, ventilation, process, bâti industriel : vos installations ouvrent droit à des primes dédiées à l'industrie. Variateurs, moteurs performants, récupération de chaleur, isolation, optimisation chaufferie. À partir de votre SIRET, on dresse la liste des opérations qui s'appliquent.",
    benefits: [
      'Utilités et bâti industriel couverts',
      'Bonus industriels en vigueur',
      'Compatible avec votre démarche énergie',
    ],
    cta: 'Lancer le diagnostic',
    trustNote: "Gratuit, Bureau d'études partenaire, Accord de confidentialité disponible",
  },
  {
    key: 'industrie-comparateur-energie',
    pageTitle: 'Comparateur énergie Industriel — Courtage HTA/HTB | Solution Éco France',
    metaDescription:
      'Optimisez vos contrats HTA/HTB de site industriel. Audit puissance, profil de consommation, mise en concurrence multi-fournisseurs.',
    eyebrow: 'Courtage énergie, Industriels',
    headline: 'Vos contrats industriels,',
    headlineAccent: 'optimisés au profil exact.',
    intro:
      "Pour vos sites industriels, on audite votre profil de consommation, on ajuste la puissance et on remet l'ensemble en concurrence sur tous les fournisseurs. Aucune bascule sans votre signature.",
    benefits: [
      'Audit du profil de consommation + puissance',
      'Mise en concurrence multi-fournisseurs',
      'Bascule planifiée, sans coupure process',
    ],
    cta: 'Demander un audit',
    trustNote:
      "Courtier indépendant des fournisseurs d'énergie · Accord de confidentialité · Référent dédié",
  },

  /* ─────────────────────────── COLLECTIVITÉS ──────────────────────────── */
  {
    key: 'collectivites-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Collectivités — Patrimoine public | Solution Éco France',
    metaDescription:
      'Estimez vos primes sur le patrimoine public (mairie, EPCI, bailleur social). Résultat chiffré par bâtiment en 60 secondes.',
    eyebrow: 'Diagnostic, Primes, Collectivités',
    headline: 'Primes patrimoine public,',
    headlineAccent: 'résultat en 60 secondes.',
    intro:
      "Mairies, gymnases, écoles, logements sociaux : votre patrimoine bâti ouvre droit à des primes. À partir de votre SIRET, on identifie les opérations qui s'appliquent et on chiffre une fourchette utilisable directement dans vos délibérations et demandes de subvention.",
    benefits: [
      'Primes patrimoine public ciblées',
      'Résultat intégrable aux délibérations',
      'Compatible commande publique',
    ],
    cta: 'Lancer le diagnostic',
    trustNote: 'Gratuit, Confidentiel, Compatible commande publique',
  },
  {
    key: 'collectivites-comparateur-energie',
    pageTitle: 'Comparateur énergie Collectivités — Marchés publics | Solution Éco France',
    metaDescription:
      "Optimisez vos achats d'énergie dans le cadre des marchés publics. Toutes les offres comparées.",
    eyebrow: 'Courtage énergie, Marchés publics, Collectivités',
    headline: 'Vos marchés énergie optimisés,',
    headlineAccent: 'dans le cadre de la commande publique.',
    intro:
      "Notre courtage est compatible avec le code de la commande publique. On audite vos contrats en cours, on compare les offres du marché et on vous accompagne jusqu'à la délibération et la notification du marché.",
    benefits: [
      'Conforme commande publique',
      'Audit des contrats en cours + alternatives',
      'Accompagnement délibération et notification',
    ],
    cta: 'Demander une pré-étude',
    trustNote: 'Gratuit, Confidentiel, Accord de confidentialité disponible',
  },
  {
    key: 'collectivites-decret-tertiaire',
    pageTitle: 'Décret Tertiaire Collectivités — Plan −40 % | Solution Éco France',
    metaDescription:
      "Calculez la trajectoire de votre patrimoine bâti tertiaire (mairie, salle polyvalente, école…) et obtenez votre plan d'actions 2030.",
    eyebrow: 'Obligation réglementaire, Collectivités',
    headline: 'Conformité de votre patrimoine,',
    headlineAccent: 'plan −40 % en 3 minutes.',
    intro:
      "Mairies, EPCI, bailleurs sociaux : vos bâtiments tertiaires de plus de 1 000 m² sont concernés. On calcule la trajectoire de chaque site et on prépare les éléments à déposer. Cumulable avec les dotations d'investissement.",
    benefits: [
      'Trajectoire bâtiment par bâtiment',
      'Éléments prêts pour le reporting annuel',
      "Compatible avec les dotations d'investissement",
    ],
    cta: 'Calculer ma trajectoire',
    trustNote: 'Gratuit, Confidentiel',
  },

  /* ─────────────────────────── COPROPRIÉTÉS ───────────────────────────── */
  {
    key: 'coproprietes-calculer-mes-aides',
    pageTitle: 'Calculer mes aides Copropriété — MPR Copro + CEE | Solution Éco France',
    metaDescription:
      "Simulez les aides MaPrimeRénov' Copropriété, CEE collectifs et éco-PTZ pour votre immeuble. Résultat par logement, prêt pour l'AG.",
    eyebrow: 'Aides rénovation, Copropriétés',
    headline: 'Vos aides copropriété chiffrées,',
    headlineAccent: 'prêtes pour votre assemblée générale.',
    intro:
      'Aides nationales à la rénovation, prime collective, prêt à taux zéro collectif, TVA réduite… on chiffre le montant total et le reste à charge par lot. Les chiffres sortent dans un format présentable en AG, pour faciliter le vote.',
    benefits: [
      'Aides nationales + primes collectives + prêt à taux zéro',
      'Reste à charge par logement calculé',
      'Présentation AG sur demande (offerte)',
    ],
    cta: 'Simuler les aides de ma copropriété',
    trustNote: 'Présentation en AG offerte',
  },
  {
    key: 'coproprietes-decret-tertiaire',
    pageTitle: 'Décret Tertiaire Copropriété — Bâtiment mixte | Solution Éco France',
    metaDescription:
      "Votre immeuble mixte (logements + commerces ou bureaux) est-il soumis à l'obligation ? Calculez la trajectoire de la partie tertiaire.",
    eyebrow: 'Obligation réglementaire, Copropriété mixte',
    headline: 'Votre immeuble est-il concerné',
    headlineAccent: "par l'obligation réglementaire ?",
    intro:
      'Si votre immeuble en copropriété abrite des commerces ou des bureaux pour plus de 1 000 m², ces surfaces sont concernées. On isole la quote-part tertiaire, on calcule les objectifs et on prépare les actions à soumettre en AG.',
    benefits: [
      'Identification surface tertiaire concernée',
      'Trajectoire de la quote-part tertiaire',
      'Recommandations pour résolution AG',
    ],
    cta: 'Vérifier mon immeuble',
    trustNote: 'Gratuit, Analyse confidentielle, Résultat sous 3 minutes',
  },
  {
    key: 'coproprietes-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Copropriété — Primes collectives | Solution Éco France',
    metaDescription:
      'Estimez les primes collectives applicables à votre copropriété (isolation façade, toiture, chaufferie, ventilation). Résultat en 60 secondes.',
    eyebrow: 'Diagnostic, Primes collectives, Copropriétés',
    headline: 'Vos primes collectives',
    headlineAccent: 'estimées pour votre AG.',
    intro:
      "Isolation de façade, toiture, chaufferie collective, ventilation : vos travaux ouvrent droit à des primes collectives, parfois bonifiées. On chiffre le potentiel et on l'intègre directement dans la note jointe à votre convocation d'AG.",
    benefits: [
      'Primes collectives (isolation, chauffage)',
      'Bonus en vigueur intégrés',
      'Note prête pour convocation AG',
    ],
    cta: 'Estimer mes primes copro',
    trustNote: 'Gratuit, Présentation AG offerte sur demande',
  },
  {
    key: 'coproprietes-comparateur-energie',
    pageTitle: 'Comparateur énergie Copropriété — Contrat collectif | Solution Éco France',
    metaDescription:
      "Comparez les offres gaz/électricité pour la chaufferie collective et les communs de votre copropriété. Courtier indépendant des fournisseurs d'énergie.",
    eyebrow: 'Courtage énergie, Contrat collectif, Copropriétés',
    headline: 'Le contrat énergie de votre copropriété,',
    headlineAccent: 'remis en concurrence.',
    intro:
      "Le contrat énergie de votre chaufferie collective et des communs pèse chaque année sur les charges. On audite vos tarifs, on compare les offres du marché, et on prépare une note prête pour le conseil syndical ou l'AG.",
    benefits: [
      'Audit chaufferie collective + communs',
      'Mise en concurrence multi-fournisseurs',
      'Note prête pour conseil syndical',
    ],
    cta: 'Demander un audit',
    trustNote: "Courtier indépendant des fournisseurs d'énergie · Sans engagement de bascule",
  },

  /* ─────────────────────────── ARTISANS ───────────────────────────────── */
  {
    key: 'artisans-cee-artisans',
    pageTitle: 'Primes CEE Artisans RGE — Estimation & dépôt | Solution Éco France',
    metaDescription:
      "Estimez vos primes selon vos fiches, transmettez vos pièces et suivez votre dossier jusqu'au paiement. 0 € d'avance, 0 € si rejet.",
    eyebrow: 'Primes, Artisans certifiés, Partenaire',
    headline: 'Estimez vos primes,',
    headlineAccent: "on s'occupe du reste.",
    intro:
      "Vous posez, on dépose. Comme partenaire, on prend tout le dossier en charge : estimation des primes, collecte des pièces, dépôt, suivi jusqu'au virement. Zéro avance, zéro frais si le dossier est rejeté.",
    benefits: [
      'Estimation des primes par opération',
      "0 € d'avance, 0 € si rejet dossier",
      'Paiement sous 30 à 60 jours',
    ],
    cta: 'Estimer mes primes',
    trustNote: 'Vérification certification automatique, Contrat signé en ligne, CGV explicites',
  },
  {
    key: 'artisans-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Artisans RGE — Fiches éligibles | Solution Éco France',
    metaDescription:
      'Identifiez les fiches CEE applicables à votre activité. Estimez vos primes potentielles avec votre SIRET.',
    eyebrow: 'Diagnostic, Primes, Artisans certifiés',
    headline: 'Quelles primes',
    headlineAccent: 'correspondent à votre activité ?',
    intro:
      'Avec votre SIRET, on identifie les primes sur lesquelles vous intervenez déjà et celles sur lesquelles vous pourriez basculer. Un premier indicateur pour préparer vos dossiers.',
    benefits: [
      'Primes adaptées à votre code NAF',
      'Fourchette de prime par opération',
      'Rapport utilisable pour votre comptabilité',
    ],
    cta: 'Lancer le diagnostic',
    trustNote: 'Gratuit, Sans engagement, Données confidentielles',
  },

  /* ─────────────────────────── AGRICULTEURS ───────────────────────────── */
  {
    key: 'agriculteurs-diagnostic-cee',
    pageTitle: 'Diagnostic CEE Agricole — Primes exploitation | Solution Éco France',
    metaDescription:
      'Estimez vos primes agricoles (séchoirs, photovoltaïque, isolation) avec votre SIRET. Résultat chiffré adapté aux fiches AGRI en 60 secondes.',
    eyebrow: 'Diagnostic, Primes agricoles, Exploitations',
    headline: 'Vos primes agricoles',
    headlineAccent: 'chiffrées en 60 secondes.',
    intro:
      "Bâtiments d'exploitation, séchoirs, chaudières, équipements photovoltaïques : ces postes ouvrent droit à des primes agricoles. À partir de votre SIRET, on liste les opérations qui s'appliquent chez vous et on chiffre une fourchette. Sans jargon.",
    benefits: [
      'Primes agricoles (séchoirs, photovoltaïque, isolation)',
      'Cumul possible avec les aides régionales',
      'Résultat sans inscription en 60 secondes',
    ],
    cta: 'Lancer le diagnostic',
    trustNote: 'Gratuit, Sans engagement, Données confidentielles',
  },
  {
    key: 'agriculteurs-cee-artisans',
    pageTitle: 'Primes CEE Agriculteurs — Estimation & dépôt | Solution Éco France',
    metaDescription:
      "Estimez vos primes agricoles et suivez votre dossier jusqu'au paiement. 0 € d'avance. Cumulable avec les aides régionales.",
    eyebrow: 'Primes, Exploitations agricoles',
    headline: 'Estimez vos primes agricoles,',
    headlineAccent: 'on gère le dossier.',
    intro:
      "Vos travaux d'isolation, vos équipements photovoltaïques, vos séchoirs ouvrent droit aux primes agricoles. On chiffre, on dépose, on suit jusqu'au virement. Pas d'avance à sortir. Cumulable avec vos aides régionales.",
    benefits: [
      'Estimation par type de travaux',
      "0 € d'avance, Cumulable avec aides régionales",
      "Suivi dossier jusqu'au paiement",
    ],
    cta: 'Estimer mes primes',
    trustNote: 'Gratuit, Cumulable avec aides régionales, Sans engagement',
  },
  {
    key: 'agriculteurs-comparateur-energie',
    pageTitle: 'Comparateur énergie Exploitation agricole | Solution Éco France',
    metaDescription:
      "Comparez les offres gaz/électricité pour votre exploitation agricole. Tarifs professionnels, audit factures gratuit, courtier indépendant des fournisseurs d'énergie.",
    eyebrow: 'Courtage énergie, Exploitations agricoles',
    headline: 'Vos factures énergie agricoles,',
    headlineAccent: 'négociées au tarif pro.',
    intro:
      'Votre exploitation accède aux tarifs professionnels. On audite vos factures actuelles, on repère les sur-puissances et on lance la mise en concurrence sur tous les fournisseurs. Bascule uniquement si vous validez.',
    benefits: [
      'Audit factures agricoles gratuit',
      'Toutes les offres pro comparées',
      'Aucune bascule automatique',
    ],
    cta: 'Comparer mes offres énergie',
    trustNote:
      "Courtier indépendant des fournisseurs d'énergie · Sans engagement · Tarifs professionnels",
  },
];

/* ─────────────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────────────── */

export function getAudienceToolContent(
  audienceId: AudienceId,
  toolId: ToolId,
): AudienceToolContent | null {
  const key = `${audienceId}-${toolId}`;
  return CONTENT.find((c) => c.key === key) ?? null;
}

export const AUDIENCE_TOOL_CONTENT = CONTENT;
